/**
 * Public privacy-policy page — hosted at /privacy, NO sign-in required.
 *
 * Content is tenant-branded and served by the backend
 * (GET /public/privacy-policy), same host-based resolution as the public
 * branding endpoint, so white-label tenants show THEIR name. The default
 * copy is a draft clearly flagged for counsel review.
 *
 * Phase 2 design system: Card + Icon, token-driven for light/dark.
 */
import { useEffect, useState } from 'react';
import { Card, Icon } from '../components/ui';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined)
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : '/api/v1';

interface PolicySection { heading: string; body: string }
interface PolicyPayload {
  company_name: string;
  version: string;
  draft: boolean;
  draft_notice: string;
  sections: PolicySection[];
}

export default function PrivacyPolicy() {
  const [policy, setPolicy] = useState<PolicyPayload | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/public/privacy-policy`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((b) => setPolicy(b as PolicyPayload))
      .catch(() => setFailed(true));
  }, []);

  return (
    <div style={page}>
      <div style={{ width: '100%', maxWidth: 720 }}>
        <Card>
          {failed ? (
            <p style={{ color: 'var(--t2)', fontSize: 14 }}>
              The privacy policy could not be loaded right now. Please try again shortly.
            </p>
          ) : !policy ? (
            <p style={{ color: 'var(--t3)', fontSize: 14 }}>Loading…</p>
          ) : (
            <>
              <h1 style={{ margin: '0 0 4px', fontSize: 24, fontWeight: 800, color: 'var(--t1)' }}>{policy.company_name} Privacy Policy</h1>
              <div style={{ fontSize: 12, color: 'var(--t3)', marginBottom: 16 }}>Version {policy.version}</div>
              {policy.draft && (
                <div style={draftBanner}>
                  <Icon name="alert" size={16} />
                  <span>{policy.draft_notice}</span>
                </div>
              )}
              {policy.sections.map((s) => (
                <section key={s.heading} style={{ marginBottom: 18 }}>
                  <h2 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: 'var(--t1)' }}>{s.heading}</h2>
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: 'var(--t2)' }}>{s.body}</p>
                </section>
              ))}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

const page: React.CSSProperties = { minHeight: '100vh', background: 'var(--sf2)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px' };
const draftBanner: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, background: 'var(--warn-tint)', border: '1px solid var(--warn-border)', color: 'var(--warn-fg)', borderRadius: 'var(--r-md)', padding: '10px 14px', fontSize: 13, fontWeight: 600, marginBottom: 20 };
